import { CaseTitle } from "@/features/case/components/CaseTitle";
import { CaseStep } from "@/features/case/components/CaseStep";
import { CaseBasicInfo } from "@/features/case/components/CaseBasicInfo/CaseBasicInfo";
import { useNavigate, useParams } from "react-router-dom";
import { useJobInfo } from "@/api/services/main/job";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Stack, Typography } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { useCaseSchema } from "@/features/case/schemas/caseSchema";
import { useForm } from "react-hook-form";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { yupResolver } from "@hookform/resolvers/yup";
import { CaseMatches } from "@/features/case/components/CaseMatches/CaseMatches";
import { useState, useEffect, useMemo } from "react";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { createCase, useCaseCount } from "@/api/services/main/case";
import { getDynamicRoute } from "@/utils/url";
import { Form } from "@/components/elements/Form";
import { generateDefaultCaseName } from "@/features/case/utils/generator";
import { InnerBox } from "@/components/elements/InnerBox";
import { Textarea } from "@/components/elements/Textarea";
import { FormField } from "@/components/elements/FormField";
import { IconPlus } from "@/assets/icons";

export const CaseCreatePage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { data: jobInfoResponse } = useJobInfo(Number(jobId));
  const jobInfo = jobInfoResponse?.data ?? {};
  const activeStep = 1;
  const urlJobDetail = AppRoutes.jobEdit.replaceAll(":jobId", String(jobId));
  const { caseSchema } = useCaseSchema();

  const { data: caseCountResponse } = useCaseCount();
  const caseCount = (caseCountResponse?.data?.count ?? 0) + 1;
  const defaultManagementNumber = useMemo(() => {
    return String(caseCount).padStart(6, "0");
  }, [caseCount]);

  const defaultValues = useMemo(() => {
    const jobName = jobInfo.jobName || "";
    const existingCases = jobInfo.cases || [];
    const defaultCaseName = generateDefaultCaseName(jobName, existingCases);
    const defaultPersonInChargeId = jobInfo.recruiter?.id || 0;

    return {
      name: defaultCaseName,
      managementNumber: defaultManagementNumber,
      personInChargeId: defaultPersonInChargeId,
      memo: "",
      talentFilter: {},
    };
  }, [jobInfo.jobName, jobInfo.cases, jobInfo.recruiter?.id, defaultManagementNumber]);

  const methods = useForm<TCaseBasicInfo>({
    defaultValues,
    resolver: yupResolver<TCaseBasicInfo>(caseSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control } = methods;

  useEffect(() => {
    if (jobInfo.jobName) {
      const jobName = jobInfo.jobName || "";
      const existingCases = jobInfo.cases || [];
      const defaultCaseName = generateDefaultCaseName(jobName, existingCases);
      const defaultPersonInChargeId = jobInfo.recruiter?.id || 0;

      methods.setValue("name", defaultCaseName);
      methods.setValue("managementNumber", defaultManagementNumber);
      if (defaultPersonInChargeId > 0) {
        methods.setValue("personInChargeId", defaultPersonInChargeId);
      }
    }
  }, [jobInfo.jobName, jobInfo.cases, jobInfo.recruiter?.id, methods, defaultManagementNumber]);

  const { watch } = methods;

  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [hasSelectedTalents, setHasSelectedTalents] = useState<boolean>(false);

  // Watch required fields
  const name = watch("name");
  const managementNumber = watch("managementNumber");
  const personInChargeId = watch("personInChargeId");

  useEffect(() => {
    const checkTalents = () => {
      if (isSelectAll) {
        setHasSelectedTalents(true);
        return;
      }
      const stringTalents = localStorage.getItem("case_match_selected") ?? "";
      if (!stringTalents) {
        setHasSelectedTalents(false);
        return;
      }
      try {
        const talents = JSON.parse(stringTalents) ?? {};
        const talentIds = Object.values(talents).flat() as number[];
        setHasSelectedTalents(talentIds.length > 0);
      } catch {
        setHasSelectedTalents(false);
      }
    };

    checkTalents();
    const interval = setInterval(checkTalents, 500);
    return () => clearInterval(interval);
  }, [isSelectAll]);

  const isButtonEnabled = useMemo(() => {
    const hasRequiredFields = name?.trim() && managementNumber?.trim() && personInChargeId && Number(personInChargeId) > 0;

    return hasRequiredFields && hasSelectedTalents;
  }, [name, managementNumber, personInChargeId, hasSelectedTalents]);

  const onSubmit = async (data: TCaseBasicInfo) => {
    if (!jobId) return;

    const caseName = data.name;
    const existingCases = jobInfo.cases || [];

    const isDuplicate = existingCases.some((caseItem) => caseItem.name === caseName);
    if (isDuplicate) {
      toast.error("案件名が既に存在します。別の案件名を入力してください。");
      return;
    }

    const basicData = {
      ...data,
      personInChargeId: Number(data.personInChargeId),
      isSelectAll,
      jobId: Number(jobId),
      talents: [] as number[],
    };

    if (!isSelectAll) {
      const stringTalents = localStorage.getItem("case_match_selected") ?? "";
      const talents = JSON.parse(stringTalents) ?? {};
      basicData.talents = Object.values(talents).flat() as number[];
    }
    try {
      const rs = await createCase(basicData);
      const route = getDynamicRoute(AppRoutes.caseEdit, { jobId: jobId });
      navigate(route);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(route);
    } catch (e) {
      showError(e);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={caseSchema}>
      <CaseTitle jobInfo={jobInfo} />
      <CaseStep activeStep={activeStep} />
      <CaseBasicInfo jobInfo={jobInfo} />
      <CaseMatches setIsSelectAll={setIsSelectAll} />
      <InnerBox mt={3} mb={3} showDivider={false} title={<Typography variant="head20Bold">案件メモ</Typography>}>
        <FormField control={control} name="memo" noLabel>
          <Textarea />
        </FormField>
      </InnerBox>
      <FooterContainer>
        <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={2}>
            <Button variant="ghost" onClick={() => navigate(urlJobDetail)}>
              戻る
            </Button>
          </Stack>
          <Stack direction="row" gap={3}>
            <Button type="submit" variant="primary" size="sm" disabled={!isButtonEnabled} startIcon={<IconPlus width={20} height={20} />}>
              案件を作成
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>
    </Form>
  );
};
